import { useState, type FormEvent } from "react";
import { Link as RouterLink } from "react-router";
import { CopyButton } from "../components/CopyButton";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  LoadingBlock,
  Mono,
  PageHeader,
  Table,
  Td,
  Th,
} from "../components/ui";
import { ApiError } from "../lib/api";
import { formatEpc, linkMetrics } from "../lib/insights";
import { formatCount, formatMoney } from "../lib/money";
import { useCreateLink, useLinks, useUpdateLink } from "../lib/queries";
import { useTranslation } from "react-i18next";

export function LinksPage() {
  const { t } = useTranslation("links");
  const [showArchived, setShowArchived] = useState(false);
  const { data: links, isPending, error } = useLinks(showArchived);
  const create = useCreateLink();
  const update = useUpdateLink();

  /*
   * Open when there is nothing to look at, closed once there is.
   *
   * A brand new affiliate lands here with an empty table and the form is
   * exactly what they need; somebody with six links wants their list. Seeded
   * from the loaded data rather than toggled by an effect, so it is right on
   * the first paint instead of appearing a frame later.
   */
  const [creating, setCreating] = useState(false);
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    try {
      await create.mutateAsync({ code: code.trim(), label: label.trim() || undefined });
      setCode("");
      setLabel("");
      // Closed on success, so what fills the screen afterwards is the link that
      // was just made rather than the empty form that made it.
      setCreating(false);
    } catch (cause) {
      // The two that actually happen — a code already in use, and a reserved
      // one — arrive as a `conflict` with a sentence written for a person, so
      // the message is shown rather than mapped.
      setFormError(cause instanceof ApiError ? cause.message : t("createFailed"));
    }
  }

  return (
    <>
      <PageHeader
        title={t("title")}
        description={t("description")}
        /*
         * Creating is the occasional act; reading the list is the constant one.
         * The form used to sit open above the table and take the top third of
         * the screen every visit, which put a control somebody uses once a
         * month above the thing they came to look at — and on a phone pushed
         * the links themselves below the fold entirely.
         */
        action={
          !creating ? (
            <Button onClick={() => setCreating(true)}>{t("new")}</Button>
          ) : undefined
        }
      />

      {creating && (
      <Card>
        <CardHeader
          title={t("form.title")}
          description={t("form.description")}
          action={
            <Button variant="ghost" size="sm" onClick={() => setCreating(false)}>
              {t("form.cancel")}
            </Button>
          }
        />
        <form onSubmit={(event) => void handleCreate(event)} className="space-y-4 px-4 py-4">
          {formError && <Alert>{formError}</Alert>}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label={t("form.code")}
              htmlFor="code"
              hint={t("form.codeHint")}
            >
              <Input
                id="code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder={t("form.codePlaceholder")}
                minLength={3}
                maxLength={40}
                required
                /* Off on all four, because a referral code is not a word: an
                   autocorrected code is a link that tracks nothing and looks
                   identical to one that works. */
                autoCapitalize="off"
                autoCorrect="off"
                autoComplete="off"
                spellCheck={false}
              />
            </Field>
            <Field label={t("form.label")} htmlFor="label" hint={t("form.labelHint")}>
              <Input
                id="label"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                placeholder={t("form.labelPlaceholder")}
                maxLength={80}
              />
            </Field>
          </div>
          <Button type="submit" loading={create.isPending} disabled={code.trim().length < 3}>
            {t("form.submit")}
          </Button>
        </form>
      </Card>
      )}

      <Card>
        <CardHeader
          title={t("yours.title")}
          description={t("yours.description")}
          action={
            <Button variant="ghost" size="sm" onClick={() => setShowArchived((on) => !on)}>
              {showArchived ? t("hideArchived") : t("showArchived")}
            </Button>
          }
        />

        {isPending ? (
          <LoadingBlock />
        ) : error ? (
          <div className="px-4 py-4">
            <Alert>{error.message}</Alert>
          </div>
        ) : links.length === 0 ? (
          <EmptyState
            title={t("empty")}
            description={t("emptyDescription")}
            action={
              !creating ? (
                <Button onClick={() => setCreating(true)}>{t("new")}</Button>
              ) : undefined
            }
          />
        ) : (
          <Table
            head={
              <tr>
                <Th>{t("th.link")}</Th>
                {/* The three numbers, pushed to the widths they survive on.
                    Clicks go first because it is the one that moves daily;
                    earnings stay to `sm` because it is what somebody opened the
                    page for and must not vanish on a phone. */}
                <Th priority="md" className="text-right">
                  {t("th.clicks")}
                </Th>
                <Th priority="md" className="text-right">
                  {t("th.signups")}
                </Th>
                {/* The rate, not just the count. Four hundred clicks and two
                    signups is a different campaign from twenty and eight, and
                    the two counts side by side make somebody do that division
                    in their head on every row. */}
                <Th priority="lg" className="text-right">
                  {t("th.perClick")}
                </Th>
                <Th className="text-right">{t("th.earned")}</Th>
                <Th className="text-right" />
              </tr>
            }
          >
            {links.map((link) => (
              <tr key={link.id} className={link.archived ? "opacity-60" : undefined}>
                <Td>
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      {/* The code is the link into the campaign screen. A
                          separate "View" button in the action column would
                          compete with Copy for the same glance, and the thing
                          somebody wants to open is the campaign. */}
                      <RouterLink
                        className="transition-colors duration-200 ease-smooth hover:text-accent"
                        to={`/links/${link.id}`}
                      >
                        <Mono className="text-fg">{link.code}</Mono>
                      </RouterLink>
                      {link.archived && (
                        <span className="text-[11px] text-fg-subtle">{t("archived")}</span>
                      )}
                    </div>
                    {link.label && <p className="text-[12px] text-fg-subtle">{link.label}</p>}
                    {/*
                     * The URL is shown from `sm` up and hidden below it, which
                     * is the one place this table bends the "never hide the
                     * only copy of a fact" rule — and it is not the only copy.
                     * The code above it is the whole of the link and the Copy
                     * button carries the exact string, so nothing here is
                     * unreachable on a phone.
                     *
                     * What it replaces is worse: at 375px the cell is about
                     120 * pixels wide and `break-all` shattered the URL into
                     * "http://loc / alhost:432 / 1/?ref=ana / ruiz", which is
                     * four lines of something nobody can read or check.
                     *
                     * `break-words` rather than `break-all` where it does show,
                     * so it breaks between path segments instead of mid-token.
                     */}
                    <p className="hidden font-mono text-[12px] break-words text-fg-muted sm:block">
                      {link.url}
                    </p>
                  </div>
                </Td>
                <Td priority="md" className="text-right tabular-nums">
                  {formatCount(link.clicks)}
                </Td>
                <Td priority="md" className="text-right tabular-nums">
                  {formatCount(link.referrals)}
                </Td>
                <Td priority="lg" className="text-right tabular-nums text-fg-muted">
                  {formatEpc(linkMetrics(link).epcMicros)}
                </Td>
                <Td className="text-right tabular-nums">{formatMoney(link.earnedMicros)}</Td>
                <Td className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <CopyButton value={link.url} />
                    <Button
                      variant="ghost"
                      size="sm"
                      loading={update.isPending && update.variables?.id === link.id}
                      onClick={() =>
                        update.mutate({ id: link.id, archived: !link.archived })
                      }
                    >
                      {link.archived ? t("restore") : t("archive")}
                    </Button>
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        )}

        {/* Said once, under the table, rather than in a confirmation nobody
            reads: archiving is not deleting, and an archived link keeps working
            for anybody who already has it. A link somebody printed last year is
            still being followed today. */}
        <p className="border-t border-line px-4 py-3 text-[12px] text-fg-subtle">
          {t("archiveNote")}
        </p>
      </Card>
    </>
  );
}
